import { useEffect, useState } from 'react';
import { useSiteMood, SiteMood } from '@/hooks/useSiteMood';

const moodStyles: Record<SiteMood, { overlay: string; animation?: string; message?: string }> = {
  calm: {
    overlay: 'bg-transparent',
    message: 'The rooms rest.',
  },
  detached: {
    overlay: 'bg-blue-950/5',
    animation: 'animate-pulse',
    message: 'Distance settles.',
  },
  curious: {
    overlay: 'bg-amber-950/5',
    message: 'Something stirs.',
  },
  unsettled: {
    overlay: 'bg-red-950/8',
    animation: 'animate-pulse',
    message: 'The shadows shift.',
  },
};

const SiteMoodOverlay = () => {
  const { mood, intensity, getMoodMessage } = useSiteMood();
  const [showMessage, setShowMessage] = useState(false);
  const [moodMessage, setMoodMessage] = useState<string | null>(null);

  useEffect(() => {
    // Occasionally show mood message (rare, mysterious)
    if (intensity > 0.6 && Math.random() < 0.15) {
      const message = getMoodMessage();
      if (message) {
        setMoodMessage(message);
        setShowMessage(true);
        setTimeout(() => setShowMessage(false), 4000);
        setTimeout(() => setMoodMessage(null), 4500);
      }
    }
  }, [mood, intensity, getMoodMessage]);

  const style = moodStyles[mood] || moodStyles.calm;

  return (
    <>
      {/* Mood overlay */}
      <div 
        className={`fixed inset-0 pointer-events-none z-30 transition-all duration-3000 ${style.overlay} ${style.animation || ''}`}
        style={{ 
          mixBlendMode: 'multiply',
          opacity: intensity * 0.5,
        }}
        aria-hidden="true"
      />

      {/* Mood message (rare) */}
      {moodMessage && (
        <div 
          className={`fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 
            text-xs text-muted-foreground/30 transition-all duration-1000 z-40 pointer-events-none
            ${showMessage ? 'opacity-100' : 'opacity-0'}`}
          aria-live="polite"
        >
          {moodMessage}
        </div>
      )}
    </>
  );
};

export default SiteMoodOverlay;
